import { Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { URLS } from '../../../data/navigation/navigation.data';
import { UtilService } from '../../../service/util/util.service';
import { placeCaret } from '../../../util/popover-caret';
import { ContactFormComponent } from '../contact-form/contact-form.component';
import { ClosableDropdown, DropdownCoordinatorService } from '../../dropdown-menu/dropdown-coordinator.service';

/** the grace before a popover the pointer opened closes, so crossing the gap to the panel keeps it (Appearance's) */
const HOVER_CLOSE_MS = 220;

/**
 * Contact Us in the top nav (owner 2026-10-12): hovering opens it like Appearance (2026-10-17), a click drops a macOS menu bar popover under it, like Dock Town's,
 * with the contact form (app-contact-form); the message goes from there, the visitor never leaves the page. The
 * Contact Us page stays for the bottom nav, a new tab and the links to it. Outside click, Escape and leaving the
 * page close it; a draft survives the close (the form group lives here). It registers with the dropdown
 * coordinator, so it, the cart and the nav menus never overlap.
 */
@Component( {
  selector: 'app-contact-popover',
  templateUrl: './contact-popover.component.html',
  styleUrls: [ './contact-popover.component.scss' ],
} )
export class ContactPopoverComponent implements OnInit, OnDestroy, ClosableDropdown {

  URLS = URLS;

  open = false;
  form: FormGroup;
  /** the form is on its thank you: the next open starts a fresh form */
  sent = false;

  private subs: Subscription[] = [];
  /** opened by the pointer resting on the item (owner 2026-10-17, as Appearance): leaving closes it */
  private hoverOpened = false;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private host: ElementRef<HTMLElement>,
    private router: Router,
    formBuilder: FormBuilder,
    private coordinator: DropdownCoordinatorService,
    private utilService: UtilService,
  ) {
    this.form = ContactFormComponent.createForm( formBuilder );
  }

  ngOnInit(): void {
    this.coordinator.register( this );
    this.subs.push(
      this.router.events.pipe( filter( event => event instanceof NavigationStart ) ).subscribe( () => this.closeDropdown() ),
    );
  }

  ngOnDestroy(): void {
    this.cancelClose();
    this.subs.forEach( sub => sub.unsubscribe() );
    this.coordinator.unregister( this );
  }

  /** the nav item: a click toggles the popover (a modified click keeps the link's own behaviour: a new tab) */
  toggle( event?: MouseEvent ): void {
    if ( event && ( event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0 ) ) return;
    event?.preventDefault();
    event?.stopPropagation();
    if ( this.open && !this.hoverOpened ) {
      this.closeDropdown();
      return;
    }
    // a click on a popover the pointer opened keeps it: it stays when the pointer leaves
    this.openPanel( false );
    this.focusFirstField();
  }

  /** the pointer rests on the item or the panel: open it (hover-capable pointers only), cancel a pending close */
  hoverOpen(): void {
    this.cancelClose();
    if ( this.open || !this.utilService.supportsHover() ) return;
    this.openPanel( true );
  }

  /** the pointer left: a popover the pointer opened closes a moment later, unless the visitor is typing in it */
  scheduleClose(): void {
    if ( !this.open || !this.hoverOpened || this.closeTimer !== null ) return;
    this.closeTimer = setTimeout( () => {
      this.closeTimer = null;
      if ( this.host.nativeElement.contains( document.activeElement ) ) return;
      this.closeDropdown();
    }, HOVER_CLOSE_MS );
  }

  private openPanel( byHover: boolean ): void {
    this.coordinator.closeOthers( this );
    this.open = true;
    this.hoverOpened = byHover;
    // the pin under the label
    setTimeout( () => {
      const element = this.host.nativeElement;
      placeCaret( element.querySelector( '.cp-panel' ), element.querySelector( '.cp-link' ) );
    }, 0 );
  }

  /** a click: the first field takes the caret, as a macOS popover's first field does (a hover never takes focus) */
  private focusFirstField(): void {
    setTimeout( () => {
      const field = this.host.nativeElement.querySelector( '.cp-panel input, .cp-panel textarea' ) as HTMLElement | null;
      field?.focus( { preventScroll: true } );
    }, 60 );
  }

  private cancelClose(): void {
    if ( this.closeTimer !== null ) {
      clearTimeout( this.closeTimer );
      this.closeTimer = null;
    }
  }

  /** ClosableDropdown: a sent message starts a fresh form next time; a draft stays */
  closeDropdown(): void {
    this.cancelClose();
    this.open = false;
    this.hoverOpened = false;
    if ( this.sent ) {
      this.sent = false;
      this.form.reset( { firstName: '', lastName: '', email: '', message: '' } );
    }
  }

  @HostListener( 'document:click', [ '$event' ] )
  onDocumentClick( event: Event ): void {
    if ( this.open && !this.host.nativeElement.contains( event.target as Node ) ) this.closeDropdown();
  }

  @HostListener( 'document:keydown.escape' )
  onEscape(): void {
    if ( this.open ) this.closeDropdown();
  }
}
